import { useMemo, useState, useRef, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import styled from 'styled-components'
import {
  ChevronLeft,
  Check,
  Search,
  X,
  LayoutList,
  Handshake,
  MailOpen,
  NotepadText,
  HandCoins,
  History,
} from 'lucide-react'
import { IoChevronDown } from 'react-icons/io5'
import { BsCalendar3 } from 'react-icons/bs'
import { ChevronUpIcon } from '@radix-ui/react-icons'
import { PiCalendarStar, PiTag } from 'react-icons/pi'
import { useInfiniteQuery } from '@tanstack/react-query'
import { format, differenceInDays } from 'date-fns'
import { MobileColors } from 'styles/mobileTokens'
import { getDaysSinceHex } from 'utils/formatting'
import { BackButton } from 'components/MobileSearch/MobileSearch.styles'
import { groupEventsByDate } from 'components/NewCompanyPage/EventHistory/utils'
import { EventIcon } from 'components/NewCompanyPage/EventHistory/EventIcon'
import { formatEventTime } from 'components/NewCompanyPage/EventHistory/utils'
import CellEventFormatterV2 from 'components/Tables/ActivityTableV2/CellEvent/CellEventFormatterV2'
import useApiService from 'services/api'
import { Spinner } from 'react-bootstrap'
import { ActivityListResponseV2, ActivityV2 } from 'models/activityV2'
import { Popover, PopoverContent, PopoverTrigger } from 'components/UI/popover'
import { Calendar } from 'components/UI/calendar'

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background: white;
`

const HeaderSection = styled.div`
  padding: 12px 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #f9fafb;
`

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const HeaderTitle = styled.h1`
  font-size: 14px;
  font-weight: 400;
  margin: 0;
  color: #191d23;
  display: flex;
  align-items: center;
  gap: 6px;
`

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const UpdatedBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 10px;
  color: #6e6e6e;
`

const DaysBadge = styled.div<{ $color: string }>`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 2px 6px;
  background: #fafafa;
  border: 1px solid #e2e2e2;
  border-radius: 999px;
  font-size: 10px;
  color: #191d23;

  &::before {
    content: '';
    display: block;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: ${(props) => props.$color};
  }
`

const FiltersSection = styled.div`
  padding: 12px 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  background: #f9fafb;
`

const FilterRow = styled.div`
  display: flex;
  gap: 8px;
`

const SearchInput = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  background: white;
  border: 1px solid #e2e2e2;
  border-radius: 8px;
  flex: 1;

  input {
    flex: 1;
    border: none;
    outline: none;
    font-size: 14px;
    color: #374151;
    background: transparent;

    &::placeholder {
      color: #9ca3af;
    }
  }

  svg {
    color: #9ca3af;
    flex-shrink: 0;
  }
`

const DateRangeButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  background: white;
  border: 1px solid #e2e2e2;
  border-radius: 8px;
  font-size: 13px;
  color: #374151;
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    border-color: ${MobileColors.primary};
  }

  svg {
    color: #6b7280;
    flex-shrink: 0;
  }
`

const ListArea = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 16px;
`

const DateGroupContainer = styled.div`
  margin-bottom: 16px;
`

const DateGroupLabel = styled.div`
  font-size: 11px;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 8px;
`

// Individual event card with green left bar
const EventCard = styled.div`
  display: flex;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
  margin-bottom: 8px;

  &:last-child {
    margin-bottom: 0;
  }
`

const GreenBar = styled.div`
  width: 4px;
  background: #c8e6c9;
  flex-shrink: 0;
`

const EventCardContent = styled.div`
  flex: 1;
  padding: 10px 12px;
  position: relative;
`

const EventHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 4px;
`

const EventDateTime = styled.div`
  font-size: 12px;
  color: #6b7280;
`

const EventIconContainer = styled.div`
  flex-shrink: 0;
  width: 28px;
  height: 28px;
  background: #f0f9f4;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #e8f5ec;
`

const EventText = styled.div`
  font-size: 13px;
  color: #6b7280;
  line-height: 1.4;

  .user-name {
    color: ${MobileColors.primary};
    font-weight: 600;
  }
`

const MoreLink = styled.button`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 4px;
  width: 100%;
  padding: 8px 0 0;
  background: transparent;
  border: none;
  font-size: 12px;
  color: ${MobileColors.primary};
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`

const DropdownContainer = styled.div`
  position: relative;
  flex: 1;
`

const DropdownButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  background: white;
  border: 1px solid #e2e2e2;
  border-radius: 8px;
  font-size: 14px;
  color: #374151;
  cursor: pointer;
  width: 100%;
  justify-content: space-between;

  &:hover {
    border-color: ${MobileColors.primary};
  }
`

const DropdownMenu = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 100;
  overflow: hidden;
`

const DropdownItem = styled.button<{ $isSelected?: boolean }>`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 12px 14px;
  background: ${(props) => (props.$isSelected ? '#f3f4f6' : 'transparent')};
  border: none;
  font-size: 14px;
  color: #374151;
  cursor: pointer;
  text-align: left;

  &:hover {
    background: #f9fafb;
  }

  svg {
    width: 18px;
    height: 18px;
    color: #6b7280;
  }
`

const CheckContainer = styled.div`
  margin-left: auto;
  color: #2c6b4e;
`

type EventTypeFilter =
  | 'all'
  | 'deal'
  | 'email'
  | 'notes'
  | 'samples'
  | 'reminder'
  | 'contactcompany'

interface EventTypeOption {
  value: EventTypeFilter
  label: string
  icon: React.ElementType
}

const EVENT_TYPE_OPTIONS: EventTypeOption[] = [
  { value: 'all', label: 'All', icon: LayoutList },
  { value: 'deal', label: 'Deals', icon: Handshake },
  { value: 'email', label: 'Emails', icon: MailOpen },
  { value: 'notes', label: 'Notes', icon: NotepadText },
  { value: 'samples', label: 'Samples', icon: HandCoins },
  { value: 'reminder', label: 'Reminders', icon: PiCalendarStar },
  { value: 'contactcompany', label: 'Account Attributes', icon: PiTag },
]

// Mobile Event Date Group component with expand/collapse
function MobileEventDateGroup({
  group,
}: {
  group: {
    date: string
    relativeDate: string
    displayDate: string
    events: ActivityV2[]
  }
}) {
  const [isExpanded, setIsExpanded] = useState(false)
  const hasMoreThan3Events = group.events.length > 3
  const eventsToShow = isExpanded ? group.events : group.events.slice(0, 3)

  return (
    <DateGroupContainer>
      {/* Date group label */}
      <DateGroupLabel>{group.relativeDate}</DateGroupLabel>

      {/* Individual event cards */}
      {eventsToShow.map((event, idx) => (
        <EventCard key={`${event.created_at}-${idx}`}>
          <GreenBar />
          <EventCardContent>
            <EventHeader>
              <EventDateTime>
                {group.displayDate} {formatEventTime(event.created_at)}
              </EventDateTime>
              <EventIconContainer>
                <EventIcon
                  contentType={event.content_type}
                  actionType={event.action_type}
                />
              </EventIconContainer>
            </EventHeader>
            <EventText>
              <span className="user-name">{event.user_name}</span>{' '}
              <CellEventFormatterV2 event={event} />
            </EventText>
          </EventCardContent>
        </EventCard>
      ))}

      {/* More/Less link */}
      {hasMoreThan3Events && (
        <MoreLink onClick={() => setIsExpanded(!isExpanded)}>
          <span>
            {isExpanded ? 'Less' : `More (+${group.events.length - 3})`}
          </span>
          <ChevronUpIcon
            className={`w-4 h-4 transition-transform ${
              isExpanded ? '' : 'rotate-180'
            }`}
          />
        </MoreLink>
      )}
    </DateGroupContainer>
  )
}

export function MobileEventsPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const api = useApiService()

  // Query params
  const contactCompanyId = searchParams.get('contact_company_id')
  const doorProxyId = searchParams.get('door_proxy_id')

  // Filter states
  const [selectedEventType, setSelectedEventType] =
    useState<EventTypeFilter>('all')
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [dateRange, setDateRange] = useState<
    { from: Date; to?: Date } | undefined
  >(undefined)
  const dropdownRef = useRef<HTMLDivElement>(null)

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false)
      }
    }

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isDropdownOpen])

  // Build query params
  const buildQueryParams = (pageParam: number) => {
    const baseParams: Record<string, unknown> = {
      page: pageParam,
      page_size: 20,
    }

    if (doorProxyId) {
      baseParams.door_proxy_id = doorProxyId
    }

    if (selectedEventType !== 'all') {
      baseParams.event_type = selectedEventType
    }

    if (dateRange?.from) {
      baseParams.start_date = format(dateRange.from, 'yyyy-MM-dd')
    }
    if (dateRange?.to) {
      baseParams.end_date = format(dateRange.to, 'yyyy-MM-dd')
    }

    return baseParams
  }

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    error,
  } = useInfiniteQuery({
    queryKey: [
      'mobileEventsPage',
      contactCompanyId,
      doorProxyId,
      selectedEventType,
      dateRange,
    ],
    queryFn: ({ pageParam }) =>
      api.getCompanyActivityList(
        Number(contactCompanyId),
        buildQueryParams(pageParam)
      ),
    initialPageParam: 1,
    getNextPageParam: (lastPage: ActivityListResponseV2, allPages) => {
      const totalFetched = allPages.reduce(
        (sum, page) => sum + page.results.length,
        0
      )
      if (totalFetched < lastPage.count) {
        return allPages.length + 1
      }
      return undefined
    },
    enabled: !!contactCompanyId,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  })

  // Flatten and group events
  const groupedEvents = useMemo(() => {
    if (!data?.pages) return []
    const allEvents = data.pages.flatMap((page) => page.results)

    // Filter out unwanted events
    let filteredEvents = allEvents.filter((event) => {
      // Filter out document events with empty field_history_objects
      if (event.content_type?.model === 'document') {
        return (
          event.field_history_objects && event.field_history_objects.length > 0
        )
      }
      // Filter out email UPDATE events
      if (
        event.content_type?.model === 'emailmessage' &&
        event.action_type === 'UPDATE'
      ) {
        return false
      }
      return true
    })

    // Client-side search filter
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase()
      filteredEvents = filteredEvents.filter(
        (event) =>
          event.user_name?.toLowerCase().includes(term) ||
          event.content_type?.model?.toLowerCase().includes(term)
      )
    }

    return groupEventsByDate(filteredEvents)
  }, [data?.pages, searchTerm])

  // Get the most recent event for header display
  const mostRecentEvent = useMemo(() => {
    if (!data?.pages?.[0]?.results?.[0]) return null
    return data.pages[0].results[0]
  }, [data?.pages])

  // Calculate days since last update
  const daysSinceUpdate = useMemo(() => {
    if (!mostRecentEvent?.created_at) return 0
    return differenceInDays(new Date(), new Date(mostRecentEvent.created_at))
  }, [mostRecentEvent])

  const handleBack = () => {
    navigate(-1)
  }

  const handleSelectEventType = (value: EventTypeFilter) => {
    setSelectedEventType(value)
    setIsDropdownOpen(false)
  }

  const selectedOption = EVENT_TYPE_OPTIONS.find(
    (opt) => opt.value === selectedEventType
  )

  return (
    <PageContainer>
      <BackButton onClick={handleBack}>
        <ChevronLeft size={20} />
        Back
      </BackButton>

      {/* Header with title and update info */}
      <HeaderSection>
        <HeaderLeft>
          <HeaderTitle>
            <History size={16} />
            EVENT HISTORY
          </HeaderTitle>
        </HeaderLeft>
        {mostRecentEvent && (
          <HeaderRight>
            <UpdatedBadge>
              <BsCalendar3 size={12} />
              Updated{' '}
              {format(new Date(mostRecentEvent.created_at), 'MM/dd/yyyy')}
            </UpdatedBadge>
            <DaysBadge $color={getDaysSinceHex(daysSinceUpdate)}>
              <span style={{ fontWeight: 600 }}>{daysSinceUpdate}</span>{' '}
              {daysSinceUpdate === 1 ? 'day' : 'days'}
            </DaysBadge>
          </HeaderRight>
        )}
      </HeaderSection>

      {/* Filters Section */}
      <FiltersSection>
        {/* Event Type Dropdown */}
        <DropdownContainer ref={dropdownRef}>
          <DropdownButton onClick={() => setIsDropdownOpen(!isDropdownOpen)}>
            <span className="flex items-center gap-2">
              {selectedOption && <selectedOption.icon size={18} />}
              {selectedOption?.label || 'All'}
            </span>
            <IoChevronDown size={18} />
          </DropdownButton>

          {isDropdownOpen && (
            <DropdownMenu>
              {EVENT_TYPE_OPTIONS.map((option) => {
                const Icon = option.icon
                const isSelected = option.value === selectedEventType
                return (
                  <DropdownItem
                    key={option.value}
                    $isSelected={isSelected}
                    onClick={() => handleSelectEventType(option.value)}
                  >
                    <Icon />
                    {option.label}
                    {isSelected && (
                      <CheckContainer>
                        <Check size={16} />
                      </CheckContainer>
                    )}
                  </DropdownItem>
                )
              })}
            </DropdownMenu>
          )}
        </DropdownContainer>

        {/* Search Input */}
        <SearchInput>
          <Search size={16} />
          <input
            type="text"
            placeholder="Search"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <X
              size={16}
              style={{ cursor: 'pointer' }}
              onClick={() => setSearchTerm('')}
            />
          )}
        </SearchInput>

        {/* Date Range Filter */}
        <FilterRow>
          <Popover>
            <PopoverTrigger asChild>
              <DateRangeButton>
                <BsCalendar3 size={14} />
                <span>
                  {dateRange?.from ? (
                    dateRange.to ? (
                      <>
                        {format(dateRange.from, 'd MMM yyyy')} -{' '}
                        {format(dateRange.to, 'd MMM yyyy')}
                      </>
                    ) : (
                      format(dateRange.from, 'd MMM yyyy')
                    )
                  ) : (
                    'All time'
                  )}
                </span>
                {dateRange && (
                  <X
                    size={14}
                    onClick={(e) => {
                      e.stopPropagation()
                      setDateRange(undefined)
                    }}
                  />
                )}
              </DateRangeButton>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                initialFocus
                mode="range"
                defaultMonth={dateRange?.from}
                selected={dateRange}
                onSelect={(newRange) => {
                  if (!newRange?.from) {
                    return
                  }
                  setDateRange(newRange as { from: Date; to?: Date })
                }}
                numberOfMonths={1}
              />
              {dateRange && (
                <div className="p-3 border-t">
                  <button
                    onClick={() => setDateRange(undefined)}
                    className="w-full px-3 py-1.5 text-sm text-gray-600 hover:text-fb-dark-green transition-colors"
                  >
                    Clear dates
                  </button>
                </div>
              )}
            </PopoverContent>
          </Popover>
        </FilterRow>
      </FiltersSection>

      <ListArea>
        {/* Loading state */}
        {isLoading && (
          <div className="flex items-center justify-center py-8">
            <Spinner animation="border" size="sm" />
          </div>
        )}

        {/* Error state */}
        {!!error && (
          <div className="py-8 text-center text-sm text-red-500">
            Failed to load event history
          </div>
        )}

        {/* Empty state */}
        {!error && groupedEvents.length === 0 && !isLoading && (
          <div className="py-8 text-center text-sm text-gray-500">
            No events found
          </div>
        )}

        {/* Content */}
        {!error && groupedEvents.length > 0 && (
          <div style={{ paddingBottom: 48 }}>
            {groupedEvents.map((group) => (
              <MobileEventDateGroup key={group.date} group={group} />
            ))}

            {/* Load More Button */}
            {hasNextPage && (
              <div className="flex justify-center pt-2 pb-4">
                <button
                  onClick={() => fetchNextPage()}
                  disabled={isFetchingNextPage}
                  className="px-4 py-2 text-sm bg-fb-dark-green text-white rounded hover:bg-fb-dark-green-hover transition-colors disabled:opacity-50"
                >
                  {isFetchingNextPage ? 'Loading...' : 'Load More'}
                </button>
              </div>
            )}
          </div>
        )}
      </ListArea>
    </PageContainer>
  )
}
